// ─────────────────────────────────────────────────────────────
// App route constants — avoid magic strings in navigate() calls
// ─────────────────────────────────────────────────────────────

export const ROUTES = {
  // Public
  HOME:             '/',
  SEARCH:           '/cerca',
  MATCHING:         '/trova-operatore',
  OPERATOR_PROFILE: '/operatori/:slug',
  BLOG:             '/blog',
  ARTICLE_DETAIL:   '/blog/:slug',
  PRIVACY:          '/privacy',
  COOKIE_POLICY:    '/cookie-policy',

  // Auth
  LOGIN:            '/login',
  LOGIN_USER:       '/login/cliente',
  LOGIN_OPERATOR:   '/login/operatore',
  REGISTER:         '/register',
  APPLY:            '/diventa-operatore',
  AUTH_CALLBACK:    '/auth/callback',
  FORGOT_PASSWORD:  '/forgot-password',

  // Operator (protected)
  OPERATOR: {
    ROOT:                  '/operator',
    DASHBOARD:             '/operator/dashboard',
    WIZARD:                '/operator/profile/wizard',
    EDIT:                  '/operator/profile/edit',
    SETTINGS:              '/operator/settings',
    SETTINGS_GOOGLE_CALENDAR_RETURN: '/operator/settings/google-calendar/return',
    SERVICES:              '/operator/services',
    CALENDAR:              '/operator/calendar',
    AVAILABILITY:          '/operator/availability',
    ARTICLES:              '/operator/articles',
    ARTICLE_NEW:           '/operator/articles/new',
    ARTICLE_EDIT:          '/operator/articles/:id/edit',
    ONBOARDING_GDPR:                  '/operator/onboarding/gdpr',
    ONBOARDING_FISCAL_DATA:           '/operator/onboarding/fiscal-data',
    ONBOARDING_SUBMIT_APPLICATION:    '/operator/onboarding/submit',
    ONBOARDING_CONTRACT:              '/operator/onboarding/contract',
    ONBOARDING_SUBSCRIPTION:          '/operator/onboarding/subscription',
    ONBOARDING_SUBSCRIPTION_OK:       '/operator/onboarding/subscription/success',
    ONBOARDING_STRIPE_CONNECT:        '/operator/onboarding/stripe-connect',
    ONBOARDING_STRIPE_CONNECT_RETURN: '/operator/onboarding/stripe-connect/return',
    BILLING:                          '/operator/billing',
    ORDERS:                           '/operator/orders',
    CLIENTS:                          '/operator/clients',
    INVOICES:                         '/operator/invoices',
    PASSIVE_INVOICES:                 '/operator/passive-invoices',
    FISCAL_DATA:                      '/operator/fiscal-data',
    REVIEWS:                          '/operator/reviews',
    BADGE:                            '/operator/badge',            // «Condividi e badge»: condivisione social + badge «Operatore verificato» + link al profilo
    MESSAGES:                         '/operator/messages',
    MESSAGES_DETAIL:                  '/operator/messages/:convId',
    COLLEAGUES:                       '/operator/colleghi',          // chat fra colleghi (sempre disponibile)
    COLLEAGUES_DETAIL:                '/operator/colleghi/:convId',
    REFERRALS:                        '/operator/referrals',
    GUIDE:                            '/operator/guide',
    ANNOUNCEMENTS:                    '/operator/announcements',
  },

  // Admin (protected)
  ADMIN: {
    ROOT:                '/admin',
    DASHBOARD:           '/admin/dashboard',
    REVIEWS:             '/admin/reviews',                // moderazione profili operatore (legacy)
    REVIEWS_MODERATION:  '/admin/reviews/moderation',     // moderazione recensioni cliente (M10)
    OPERATORS:           '/admin/operators',
    OPERATOR_DETAIL:     '/admin/operators/:id',
    APPOINTMENTS:        '/admin/appointments',         // storico appuntamenti filtrabile
    USERS:               '/admin/users',
    MATCHING:            '/admin/matching',          // statistiche quiz "Trova il tuo operatore"
    USER_DETAIL:         '/admin/users/:id',
    INVITES:             '/admin/invites',
    LEADS:               '/admin/leads',             // potenziali operatori da ricontattare
    EVENTS:              '/admin/eventi',            // elenco eventi dal vivo (+ crea)
    EVENT_DETAIL:        '/admin/eventi/:eventId',   // adesioni a un evento (RSVP landing)
    AUTOMATIONS:         '/admin/automations',       // promemoria, job ricorrenti, scadenze
    EMAIL_LOG:           '/admin/email',             // log delle email inviate dal sistema (?to=)
    TEMPLATES:           '/admin/templates',
    ANNOUNCEMENTS:       '/admin/announcements',
    SUPPORT:             '/admin/supporto',          // casella condivisa «Supporto OLLYlife»
    SUPPORT_DETAIL:      '/admin/supporto/:convId',
    ACCOUNTING:          '/admin/accounting',
    ARTICLES:            '/admin/articles',
    ARTICLES_REVIEW:     '/admin/articles/reviews',
    ARTICLE_NEW:         '/admin/articles/new',
    ARTICLE_DETAIL:      '/admin/articles/:id',
  },

  // User (protected — Fase 2)
  USER: {
    ROOT:            '/user',
    DASHBOARD:       '/user/dashboard',
    SESSIONS:        '/user/sessions',
    REVIEWS:         '/user/reviews',
    SAVED:           '/user/saved',
    PROFILE:         '/user/profile',
    BOOKINGS:        '/user/bookings',
    MESSAGES:        '/user/messages',
    MESSAGES_DETAIL: '/user/messages/:convId',
  },

  // Videocall (protected, USER o OPERATOR dell'appointment)
  VIDEOCALL: '/videocall/:appointmentId',
} as const

/** Build a dynamic operator profile URL */
export const operatorProfileUrl = (slug: string) =>
  ROUTES.OPERATOR_PROFILE.replace(':slug', slug)

/** Build the admin detail page URL for a given operator id */
export const adminOperatorDetailUrl = (operatorId: string) =>
  ROUTES.ADMIN.OPERATOR_DETAIL.replace(':id', operatorId)

/** Build the admin page of a live event (RSVP list) */
export const adminEventDetailUrl = (eventId: string) =>
  ROUTES.ADMIN.EVENT_DETAIL.replace(':eventId', encodeURIComponent(eventId))

/** Email inviate a un indirizzo (log email admin, filtrato per destinatario) */
export const adminEmailLogUrl = (email: string) =>
  `${ROUTES.ADMIN.EMAIL_LOG}?to=${encodeURIComponent(email.trim().toLowerCase())}`

/** Build the admin detail page URL for a given user id */
export const adminUserDetailUrl = (userId: string) =>
  ROUTES.ADMIN.USER_DETAIL.replace(':id', userId)

/** Operator article edit URL */
export const operatorArticleEditUrl = (id: string) =>
  ROUTES.OPERATOR.ARTICLE_EDIT.replace(':id', id)

/** Admin article detail URL */
export const adminArticleDetailUrl = (id: string) =>
  ROUTES.ADMIN.ARTICLE_DETAIL.replace(':id', id)

/** Videocall room URL per un appointment */
export const videocallUrl = (appointmentId: string) =>
  ROUTES.VIDEOCALL.replace(':appointmentId', appointmentId)
